import { ReactNode, useLayoutEffect, useRef } from "react";
import cn from "classnames";
import { Divider, Badge, CircularProgress, Stack, Box } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { first, last, orderBy } from "lodash";

import Highlighter from "react-highlight-words";

import {
  MonitoringIssue,
  IssueStatus,
  Monitor,
  MonitorActivityType,
  MonitorActivity,
} from "../../fixtures";

import AssignButton from "./AssignButton";
import ResolveButton from "./ResolveButton";
import MuteButton from "./MuteButton";
import LinkButton from "./LinkButton";
import CommentsAndActivity from "./CommentsAndActivity";

import {
  formatDateTime,
  formatStatus,
  formatUser,
  formatDate,
  partitionBy,
  formatMonitorActivity,
} from "./utils";

import "./Detail.css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getAllIssueActivity,
  getAllPeople,
  getIssue,
  getMonitor,
  getMonitorIssues,
} from "./queries";
import { deleteMonitor, unDeleteMonitor } from "../mutations";

const Highlight = ({
  children,
}: {
  children: ReactNode;
  highlightIndex: number;
}) => <strong className="highlighted-text">{children}</strong>;

function IssueDetail({ issue }: { issue: MonitoringIssue }) {
  return (
    <div
      className={cn(
        {
          unresolved: ![IssueStatus.Resolved, IssueStatus.Muted].includes(
            issue.status,
          ),
        },
        "issue",
      )}
    >
      <div className="badge">
        <div className="date">{formatDateTime(issue.date)}</div>
        <div className="status">{formatStatus(issue.status)}</div>
      </div>
      <div className="controls">
        <div className="assignee">
          Assignee:{" "}
          <span className={cn("name", { empty: !issue.assignee })}>
            {!issue.assignee ? "unassigned" : formatUser(issue.assignee)}
          </span>
        </div>
        <div className="actionButtons">
          <LinkButton title="Copy link" issueId={issue.id} />
          <AssignButton
            title="Assign issue"
            issueId={issue.id}
            currentAssigneeId={issue.assignee?.id}
          />
          <MuteButton title="Mute issue" issue={issue} />
          <ResolveButton
            title={
              issue.status === IssueStatus.Resolved
                ? "This issue has already been resolved"
                : "Resolve issue"
            }
            issueId={issue.id}
            disabled={issue.status === IssueStatus.Resolved}
          />
        </div>
      </div>
      <div className="details">
        <div className="summary">
          {issue.alerts.map((alert, idx) => (
            <div key={idx} className="alert">
              <div className="type">
                Alert type(s): <strong>{alert.type.sort().join(", ")}</strong>
              </div>
              <div className="description">
                Content related to this was found
                {alert.citations.length > 0 && " on the following pages:"}
              </div>
              <ul className="citations">
                {alert.citations.map((citation) => (
                  <li key={citation.url} className="citation">
                    <a
                      href={citation.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {citation.url}
                    </a>
                    <div className="context">
                      <div className="quote">
                        <Highlighter
                          searchWords={citation.highlights.map(
                            (highlight) => highlight.summary,
                          )}
                          textToHighlight={citation.context}
                          highlightTag={Highlight}
                        />
                      </div>
                      <div className="firstSeen">
                        First seen {formatDate(citation.firstSeen)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <Divider orientation="vertical" flexItem />
        <CommentsAndActivity issueId={issue.id} />
      </div>
    </div>
  );
}

interface MonitorTimelineProps {
  monitor: Monitor;
  issues: MonitoringIssue[];
  highlightedIssueId?: string;
}

function MonitorTimeline({ monitor, issues }: MonitorTimelineProps) {
  const queryClient = useQueryClient();

  const unresolvedIssueCount = issues.filter(
    ({ status }) => ![IssueStatus.Resolved, IssueStatus.Muted].includes(status),
  ).length;

  const enrollmentMutation = useMutation({
    mutationFn: async ({
      deleted,
      monitorId,
    }: {
      deleted: boolean;
      monitorId: string;
    }) => {
      if (deleted) {
        return deleteMonitor(monitorId);
      } else {
        return unDeleteMonitor(monitorId);
      }
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: ["getMonitor", variables.monitorId],
      });
      const previous = queryClient.getQueryData([
        "getMonitor",
        variables.monitorId,
      ]);

      queryClient.setQueryData(
        ["getMonitor", variables.monitorId],
        (old: any) => {
          const updated = {
            ...old,
            monitor: {
              ...old.monitor,
              _deleted: variables.deleted,
            },
          };

          return updated;
        },
      );

      return { previous };
    },
    onError: (err, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          ["getMonitor", variables.monitorId],
          context.previous,
        );
      }
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["getMonitor", variables.monitorId],
      });

      queryClient.invalidateQueries({
        queryKey: ["allMonitors"],
      });
    },
    mutationKey: ["toggleMonitorEnrollment", monitor.id, monitor._deleted],
  });

  return (
    <div className="timeline">
      <div className="header">
        <h2 style={{ marginTop: 0, marginBottom: "0.25em" }}>
          <span>{monitor.domain}</span>
          <LoadingButton
            variant="outlined"
            size="small"
            sx={{ ml: "1em" }}
            loading={enrollmentMutation.isPending}
            onClick={async () => {
              await enrollmentMutation.mutateAsync({
                monitorId: monitor.id,
                deleted: !monitor._deleted,
              });
            }}
          >
            {monitor._deleted ? "Re-" : "Un"}enroll
          </LoadingButton>
        </h2>
        <div className="meta">
          {unresolvedIssueCount > 0 && (
            <div
              style={{
                display: "flex",
                gap: "0.25em",
              }}
            >
              <Badge
                color="primary"
                sx={{
                  "& .MuiBadge-badge": {
                    border: "1px solid currentcolor",
                    position: "initial",
                    transform: "translateY(2px)",
                  },
                }}
                badgeContent={unresolvedIssueCount}
              />
              <div>
                <strong>
                  Open Issue{unresolvedIssueCount === 1 ? "" : "s"}
                </strong>
              </div>
            </div>
          )}
          <div className="runInfo">
            <div className="next">
              Next Run:{" "}
              {monitor.nextRun ? (
                <strong>{formatDate(monitor.nextRun)}</strong>
              ) : (
                "unscheduled"
              )}
            </div>
            <div className="previous">
              Previous Run:{" "}
              {monitor.lastRun ? (
                <strong>{formatDate(monitor.lastRun)}</strong>
              ) : (
                "never"
              )}
            </div>
          </div>
        </div>
      </div>

      {/* TODO: re-integrate infinite pagination for newer items */}

      <Stack alignItems="center" width="90%">
        {/* TODO: un-abominafy. this allows for interspersing issues with activity, as well as de-duping repeated activity */}
        {partitionBy(
          orderBy([...monitor.activity, ...issues], ["date"], ["desc"]),
          (activityOrIssue: MonitorActivity | MonitoringIssue) => {
            if ("monitorId" in activityOrIssue) {
              // issue
              return `issue`;
            }

            if (activityOrIssue.type === MonitorActivityType.HomepageScan) {
              return `${activityOrIssue.type}__${activityOrIssue.detectedIssueIds.length}`;
            }

            if (activityOrIssue.type === MonitorActivityType.FullScan) {
              return `${activityOrIssue.type}__${activityOrIssue.detectedIssueIds.length}`;
            }

            return activityOrIssue.type;
          },
        ).map((activityGroup, i, allGroups) => {
          if (!activityGroup.length) return null;

          const activityOrIssue = activityGroup[0];

          if ("monitorId" in activityOrIssue) {
            // issue
            return activityGroup.map((issue) => (
              <IssueDetail
                issue={issue as unknown as MonitoringIssue}
                key={issue.id}
              />
            ));
          }

          const sortedGroup = orderBy(activityGroup, ["date"], ["asc"]);
          const earliestDate = first(sortedGroup)?.date;
          const latestDate = last(sortedGroup)?.date;

          return (
            <div
              key={`${activityGroup}__${i}`}
              className={cn("activitySummary", {
                collapsed: activityGroup.length > 1,
              })}
            >
              {activityGroup.length === 1 && (
                <div className="date">{formatDate(earliestDate!)}</div>
              )}
              {activityGroup.length > 1 && (
                <div className="date">
                  {formatDate(earliestDate!)} &ndash; {formatDate(latestDate!)}
                </div>
              )}
              <div className="description">
                {activityGroup.length > 1 && `${activityGroup.length} `}
                {formatMonitorActivity(activityOrIssue, activityGroup.length)}
              </div>
              {i !== allGroups.length - 1 && (
                <div style={{ textAlign: "center", color: "#dad9d9" }}>|</div>
              )}
            </div>
          );
        })}
      </Stack>

      {/* TODO: re-integrate infinite pagination for older items */}
    </div>
  );
}

function BigLoader() {
  // TODO: unify these loaders using suspense
  return (
    <Box
      display="flex"
      minHeight="300px"
      alignItems="center"
      justifyContent="center"
      flex={1}
    >
      <CircularProgress />
    </Box>
  );
}

export interface MonitorDetail {
  monitorId: string;
  highlightedIssueId?: string;
}

function MonitorDetail({ monitorId, highlightedIssueId }: MonitorDetail) {
  const monitorQuery = useQuery({
    queryFn: () => getMonitor(monitorId),
    queryKey: ["getMonitor", monitorId],
  });

  const issuesQuery = useQuery({
    queryFn: () => getMonitorIssues(monitorId),
    queryKey: ["getMonitorIssues", monitorId],
  });

  if (monitorQuery.isLoading || issuesQuery.isLoading) {
    return <BigLoader />;
  }

  if (
    monitorQuery.isError ||
    issuesQuery.isError ||
    !monitorQuery.data ||
    !issuesQuery.data
  ) {
    return <div>There was an error completing this request.</div>;
  }

  return (
    <MonitorTimeline
      monitor={monitorQuery.data.monitor}
      issues={issuesQuery.data.issues}
      highlightedIssueId={highlightedIssueId}
    />
  );
}

export interface Props {
  issueId: string;
}

function _DetailView({ issueId }: Props) {
  // prevent the dialog from shrinking during the close animation
  const lastIssueId = useRef(issueId);

  useLayoutEffect(() => {
    if (!issueId) return;
    lastIssueId.current = issueId;
  }, [issueId]);

  // Prefetch a shitton of stuff and hydrate the cache
  // TODO: figure out a way to nicely handle this in a composable way

  const issueQuery = useQuery({
    queryFn: () => getIssue(issueId),
    queryKey: ["getIssue", issueId],
    enabled: !!issueId,
  });

  const activityQuery = useQuery({
    queryFn: () => getAllIssueActivity(issueId),
    queryKey: ["issueActivity", issueId],
    enabled: !!issueId,
  });

  const allPeopleQuery = useQuery({
    queryFn: getAllPeople,
    queryKey: ["allPeople"],
  });

  const monitorQuery = useQuery({
    queryFn: async () => {
      if (!issueQuery.data?.issue.monitorId) {
        throw new Error("No monitor id");
      }
      return getMonitor(issueQuery.data.issue.monitorId);
    },
    queryKey: ["getMonitor", issueQuery.data?.issue.monitorId],
    enabled: !!issueQuery.data,
  });

  const issuesQuery = useQuery({
    queryFn: async () => {
      if (!issueQuery.data?.issue.monitorId) {
        throw new Error("No monitor id");
      }
      return getMonitorIssues(issueQuery.data?.issue.monitorId);
    },
    queryKey: ["getMonitorIssues", issueQuery.data?.issue.monitorId],
    enabled: !!issueQuery.data,
  });

  if (!lastIssueId.current) return null;

  if (
    issueQuery.isLoading ||
    activityQuery.isLoading ||
    allPeopleQuery.isLoading ||
    monitorQuery.isLoading ||
    issuesQuery.isLoading
  ) {
    <BigLoader />;
  }

  if (
    !issueQuery.data ||
    !activityQuery.data ||
    !monitorQuery.data ||
    !issuesQuery.data
  ) {
    return <BigLoader />;
  }

  return (
    <MonitorDetail
      monitorId={issueQuery.data.issue.monitorId}
      highlightedIssueId={issueId}
    />
  );
}

export default function DetailView({ issueId }: Props) {
  return (
    <div
      style={{
        minHeight: "600px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <_DetailView issueId={issueId} />
    </div>
  );
}
